import { ReactNode, useEffect, useRef } from 'react'
import clsx from 'clsx'

import styles from './Modal.module.scss'

import CrossIcon from './cross.svg'
interface ModalProps {
    onClose: () => void
    hideCloseButton?: boolean
    doNotCloseOnClickOutside?: boolean
    className?: string
    children: ReactNode
}

export const Modal = ({
    onClose,
    hideCloseButton = false,
    doNotCloseOnClickOutside = false,
    className,
    children,
}: ModalProps) => {
    const modalRef = useRef<HTMLDialogElement>(null)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    const handleCloseModal = () => {
        if (onClose) {
            onClose()
        }
    }

    const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
        // Close if clicking the backdrop (outside of the modal content)
        if (e.target === e.currentTarget && !doNotCloseOnClickOutside) {
            handleCloseModal()
        }
    }

    useEffect(() => {
        const modalElement = modalRef.current
        if (!modalElement) return

        modalElement.showModal()

        // Handle Escape directly on the dialog element (not via React's onKeyDown)
        // so preventDefault() is called early during bubbling, before the browser
        // can remove the dialog from the top layer.
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault()
                e.stopPropagation()
                onCloseRef.current()
            }
        }

        // Safety net: if cancel still fires, prevent the native close.
        const handleCancel = (e: Event) => {
            e.preventDefault()
        }

        modalElement.addEventListener('keydown', handleEscape)
        modalElement.addEventListener('cancel', handleCancel)

        return () => {
            modalElement.removeEventListener('keydown', handleEscape)
            modalElement.removeEventListener('cancel', handleCancel)
        }
    }, [])

    return (
        <dialog
            className={clsx(className, styles.modal)}
            ref={modalRef}
            onClick={handleBackdropClick}
        >
            {!hideCloseButton && (
                <button className={styles.modalCloseButton} onClick={handleCloseModal} aria-label="Close modal">
                    <CrossIcon />
                </button>
            )}
            {children}
        </dialog>
    )
}
